/**
 * Event Ranking Criteria Catalogue (#48)
 * 
 * Defines all available ranking criteria and tie-breakers across Matches, Games,
 * Serve, Pressure, Classic, and Form groups.
 */

import {
  isClassicSession,
  isRotationSession,
  isCompletedClassicMatch,
  getClassicTeams,
  getRotationMatchOutcome,
  isRankedSession,
} from './matchStats.js';

const toPlayerName = (p) => {
  if (!p) return '';
  if (typeof p === 'object') return p.name || p.id || p.uid || '';
  return String(p).trim();
};

const toPlayerArray = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val.map(toPlayerName).filter(Boolean);
  if (typeof val === 'object') {
    const vals = Object.values(val);
    if (vals.length > 0) return vals.map(toPlayerName).filter(Boolean);
  }
  if (typeof val === 'string') {
    const s = val.trim();
    return s && s.toLowerCase() !== 'tie' ? [s] : [];
  }
  return [];
};

/** Normalize score points value (e.g. 4, '40', 3, '30') */
const parsePoints = (pts) => {
  if (pts === null || pts === undefined) return 0;
  if (typeof pts === 'number') return pts;
  const s = String(pts).trim();
  if (s === '40') return 3;
  if (s === '30') return 2;
  if (s === '15') return 1;
  const n = parseInt(s, 10);
  return Number.isNaN(n) ? 0 : n;
};

/**
 * Checks if a player was the server (or in the serving team) for a game.
 */
function isPlayerServing(game, playerName, team1, team2) {
  if (!game || !playerName) return false;
  const server = game.serving || game.server;

  // Direct server name or object match
  if (server) {
    if (typeof server === 'string') {
      const lowerServer = server.toLowerCase().trim();
      const lowerName = playerName.toLowerCase().trim();
      if (lowerServer === lowerName) return true;
      if (lowerServer.includes(lowerName)) return true;
    } else if (typeof server === 'object') {
      const sName = toPlayerName(server).toLowerCase().trim();
      if (sName === playerName.toLowerCase().trim()) return true;
    }
  }

  // Check servingTeam number (1 or 2) in classic match
  const servingTeamNum = game.servingTeam ?? game.servingTeamNumber;
  if (servingTeamNum === 1 && team1.some((p) => p.toLowerCase().trim() === playerName.toLowerCase().trim())) return true;
  if (servingTeamNum === 2 && team2.some((p) => p.toLowerCase().trim() === playerName.toLowerCase().trim())) return true;

  return false;
}

/**
 * Checks if a game has any designated server.
 */
function hasServerDesignated(game) {
  if (!game) return false;
  return Boolean(game.serving || game.server || game.servingTeam !== undefined || game.servingTeamNumber !== undefined);
}

/**
 * Checks if a player was receiving in a game.
 * Correctly distinguishes opponent serving vs partner serving in doubles (#48).
 */
function isPlayerReceiving(game, playerName, team1, team2) {
  if (!game || !playerName || !hasServerDesignated(game)) return false;
  const pName = playerName.toLowerCase().trim();

  // If player is the server, they are not receiving
  if (isPlayerServing(game, playerName, team1, team2)) return false;

  const server = game.serving || game.server;
  let serverName = '';
  if (server) {
    serverName = (typeof server === 'object' ? toPlayerName(server) : String(server)).toLowerCase().trim();
  }

  // 1. Classic match: check team1 vs team2
  const inTeam1 = team1.some((p) => p.toLowerCase().trim() === pName);
  const inTeam2 = team2.some((p) => p.toLowerCase().trim() === pName);
  const servingTeamNum = game.servingTeam ?? game.servingTeamNumber;

  if (inTeam1 || inTeam2) {
    let serverTeam = servingTeamNum;
    if (!serverTeam && serverName) {
      if (team1.some((p) => p.toLowerCase().trim() === serverName)) serverTeam = 1;
      else if (team2.some((p) => p.toLowerCase().trim() === serverName)) serverTeam = 2;
    }

    if (serverTeam === 1) {
      return inTeam2; // Team 2 is receiving
    }
    if (serverTeam === 2) {
      return inTeam1; // Team 1 is receiving
    }
  }

  // 2. Rotation or game with winningTeam and losingTeam
  const winners = toPlayerArray(game.winningTeam);
  const losers = toPlayerArray(game.losingTeam);
  const serverInWinners = serverName && winners.some((p) => p.toLowerCase().trim() === serverName);
  const serverInLosers = serverName && losers.some((p) => p.toLowerCase().trim() === serverName);

  if (serverInWinners) {
    return losers.some((p) => p.toLowerCase().trim() === pName);
  }
  if (serverInLosers) {
    return winners.some((p) => p.toLowerCase().trim() === pName);
  }

  // Fallback: designated server that is not the player
  return Boolean(serverName && serverName !== pName);
}

/**
 * Extract games from a session in chronological order.
 */
function getSessionGames(session) {
  if (!session) return [];
  if (Array.isArray(session.classicGameHistory) && session.classicGameHistory.length > 0) {
    return session.classicGameHistory;
  }
  if (Array.isArray(session.sessionHistory) && session.sessionHistory.length > 0) {
    return session.sessionHistory;
  }
  return [];
}

/**
 * Extract set scores from a classic session.
 * Returns array of [team1Games, team2Games].
 */
function getClassicSetScores(session) {
  if (!session) return [];
  const rawScores = session.setScores || session.matchSummary?.setScores || session.classicScore?.setScores || [];
  return rawScores.map((s) => {
    if (Array.isArray(s)) return [Number(s[0]) || 0, Number(s[1]) || 0];
    if (s && typeof s === 'object') {
      const g1 = s.team1 ?? s.games?.[0] ?? s[0] ?? 0;
      const g2 = s.team2 ?? s.games?.[1] ?? s[1] ?? 0;
      return [Number(g1) || 0, Number(g2) || 0];
    }
    return [0, 0];
  }).filter(([g1, g2]) => g1 > 0 || g2 > 0);
}

const isSetWon = (w, l) => (w >= 6 && w - l >= 2) || (w === 7 && l === 6);

/**
 * Compute all raw metrics for a player across an array of eligible sessions in a single pass.
 */
export function computePlayerStatsForEvent(sessions = [], playerName) {
  if (!playerName) {
    return {
      w: 0, l: 0, mp: 0, mvp: 0, winPct: 0,
      gw: 0, gl: 0, gp: 0, gameDiff: 0, gameWinPct: 0,
      holdWins: 0, totalServes: 0, holdPct: 0,
      breakWins: 0, totalReceives: 0, breakPct: 0,
      goldenPointWins: 0, loveGameWins: 0,
      setWins: 0, setLosses: 0, setDiff: 0, tiebreakWins: 0,
      longestMatchStreak: 0, longestGameStreak: 0,
    };
  }

  const pName = playerName.toLowerCase().trim();

  let w = 0;
  let l = 0;
  let mvp = 0;
  let gw = 0;
  let gl = 0;
  let holdWins = 0;
  let totalServes = 0;
  let breakWins = 0;
  let totalReceives = 0;
  let goldenPointWins = 0;
  let loveGameWins = 0;
  let setWins = 0;
  let setLosses = 0;
  let tiebreakWins = 0;

  // Track match outcomes chronologically for longestMatchStreak
  const matchOutcomes = []; // Array of boolean: true = win, false = loss
  // Track all game outcomes chronologically for longestGameStreak
  const allGameOutcomes = []; // Array of boolean: true = win, false = loss

  // Sort sessions chronologically (oldest first)
  const sortedSessions = [...sessions].sort((a, b) => {
    const tA = a.timestamp || (a.date ? new Date(a.date).getTime() : 0);
    const tB = b.timestamp || (b.date ? new Date(b.date).getTime() : 0);
    return tA - tB;
  });

  sortedSessions.forEach((session) => {
    const isClassic = isClassicSession(session);
    let playerWonMatch = false;
    let playerLostMatch = false;

    if (isClassic) {
      if (isCompletedClassicMatch(session)) {
        const [winners, losers] = getClassicTeams(session);
        const inWinners = winners.some((p) => p.toLowerCase().trim() === pName);
        const inLosers = losers.some((p) => p.toLowerCase().trim() === pName);
        if (inWinners) {
          w += 1;
          playerWonMatch = true;
        } else if (inLosers) {
          l += 1;
          playerLostMatch = true;
        }
      }

      // Sets won and lost in classic
      const [team1, team2] = [
        toPlayerArray(session.team1 || session.matchSummary?.team1 || session.sessionPlayers?.slice(0, 2)),
        toPlayerArray(session.team2 || session.matchSummary?.team2 || session.sessionPlayers?.slice(2, 4)),
      ];
      const isTeam1 = team1.some((p) => p.toLowerCase().trim() === pName);
      const isTeam2 = team2.some((p) => p.toLowerCase().trim() === pName);

      if (isTeam1 || isTeam2) {
        const setScores = getClassicSetScores(session);
        setScores.forEach(([g1, g2]) => {
          if (isTeam1) {
            if (isSetWon(g1, g2)) setWins += 1;
            else if (isSetWon(g2, g1)) setLosses += 1;
            if (g1 === 7 && g2 === 6) tiebreakWins += 1;
          } else {
            if (isSetWon(g2, g1)) setWins += 1;
            else if (isSetWon(g1, g2)) setLosses += 1;
            if (g2 === 7 && g1 === 6) tiebreakWins += 1;
          }
        });
      }
    } else {
      // Rotation session
      const outcome = getRotationMatchOutcome(session);
      if (outcome) {
        const inWinners = outcome.winners.some((p) => p.toLowerCase().trim() === pName);
        const inLosers = outcome.losers.some((p) => p.toLowerCase().trim() === pName);
        if (inWinners) {
          w += 1;
          playerWonMatch = true;
        } else if (inLosers) {
          l += 1;
          playerLostMatch = true;
        }

        if (outcome.mvpPlayer && outcome.mvpPlayer.toLowerCase().trim() === pName) {
          mvp += 1;
        }
      }
    }

    if (playerWonMatch) {
      matchOutcomes.push(true);
    } else if (playerLostMatch) {
      matchOutcomes.push(false);
    }

    // Process games in this session
    const games = getSessionGames(session);
    const [team1, team2] = isClassic
      ? [
          toPlayerArray(session.team1 || session.matchSummary?.team1),
          toPlayerArray(session.team2 || session.matchSummary?.team2),
        ]
      : [[], []];

    if (games.length > 0) {
      games.forEach((game) => {
        if (!game) return;
        const winners = toPlayerArray(game.winningTeam);
        const losers = toPlayerArray(game.losingTeam);
        const inWinners = winners.some((p) => p.toLowerCase().trim() === pName);
        const inLosers = losers.some((p) => p.toLowerCase().trim() === pName);

        if (!inWinners && !inLosers) return; // Player did not play this game

        if (inWinners) {
          gw += 1;
          allGameOutcomes.push(true);
        } else if (inLosers) {
          gl += 1;
          allGameOutcomes.push(false);
        }

        // Serve and Break check (#48)
        const serving = isPlayerServing(game, playerName, team1, team2);
        const receiving = isPlayerReceiving(game, playerName, team1, team2);

        if (serving) {
          totalServes += 1;
          if (inWinners) {
            holdWins += 1;
          }
        } else if (receiving) {
          totalReceives += 1;
          if (inWinners) {
            breakWins += 1;
          }
        }

        // Pressure check: Golden point & Love game
        const scores = game.scores || {};
        let winPts = 0;
        let losePts = 0;

        if (scores.winningTeam !== undefined && scores.losingTeam !== undefined) {
          winPts = parsePoints(scores.winningTeam);
          losePts = parsePoints(scores.losingTeam);
        } else if (scores.team1 !== undefined && scores.team2 !== undefined) {
          const t1Won = winners.some((p) => team1.some((t) => t.toLowerCase().trim() === p.toLowerCase().trim()));
          winPts = t1Won ? parsePoints(scores.team1) : parsePoints(scores.team2);
          losePts = t1Won ? parsePoints(scores.team2) : parsePoints(scores.team1);
        }

        const isGoldenPoint =
          Boolean(game.isGoldenPoint) ||
          Boolean(game.goldenPoint) ||
          losePts >= 3 ||
          String(scores.losingTeam).trim() === '40';

        const isLoveGame =
          losePts === 0 && (winPts >= 3 || String(scores.winningTeam).trim() === '40');

        if (inWinners) {
          if (isGoldenPoint) goldenPointWins += 1;
          if (isLoveGame) loveGameWins += 1;
        }

        // Tiebreak in classic games
        if (isClassic && (game.isTieBreak || game.tieBreak)) {
          if (inWinners) {
            // Note: only add if not already captured by set score (7-6 adds at set level if recorded)
            // But if individual tiebreak games are tracked, ensure tiebreakWins counts
          }
        }
      });
    } else if (session.sessionStats && session.sessionStats[playerName]) {
      // Fallback for games when no individual game list is recorded
      const st = session.sessionStats[playerName];
      gw += Number(st?.wins) || 0;
      gl += Number(st?.losses) || 0;
    }
  });

  // Calculate streaks
  let currentMatchStreak = 0;
  let longestMatchStreak = 0;
  matchOutcomes.forEach((win) => {
    if (win) {
      currentMatchStreak += 1;
      if (currentMatchStreak > longestMatchStreak) longestMatchStreak = currentMatchStreak;
    } else {
      currentMatchStreak = 0;
    }
  });

  let currentGameStreak = 0;
  let longestGameStreak = 0;
  allGameOutcomes.forEach((win) => {
    if (win) {
      currentGameStreak += 1;
      if (currentGameStreak > longestGameStreak) longestGameStreak = currentGameStreak;
    } else {
      currentGameStreak = 0;
    }
  });

  const mp = w + l;
  const gp = gw + gl;
  const winPct = mp > 0 ? w / mp : 0;
  const gameWinPct = gp > 0 ? gw / gp : 0;
  const gameDiff = gw - gl;
  const holdPct = totalServes > 0 ? holdWins / totalServes : 0;
  const breakPct = totalReceives > 0 ? breakWins / totalReceives : 0;
  const setDiff = setWins - setLosses;

  return {
    w,
    l,
    mp,
    mvp,
    winPct,
    gw,
    gl,
    gp,
    gameDiff,
    gameWinPct,
    holdWins,
    totalServes,
    holdPct,
    breakWins,
    totalReceives,
    breakPct,
    goldenPointWins,
    loveGameWins,
    setWins,
    setLosses,
    setDiff,
    tiebreakWins,
    longestMatchStreak,
    longestGameStreak,
  };
}

/**
 * Registry of all available event criteria.
 */
export const CRITERIA_CATALOGUE = {
  // --- MATCHES ---
  matchWins: {
    id: 'matchWins',
    label: 'Match wins',
    group: 'Matches',
    description: 'Total matches won',
    modes: ['rotation', 'classic'],
    direction: 'desc',
    format: (v) => String(v ?? 0),
    compute: (stats) => stats.w ?? 0,
  },
  matchWinPct: {
    id: 'matchWinPct',
    label: 'Match win %',
    group: 'Matches',
    description: 'Percentage of matches won',
    modes: ['rotation', 'classic'],
    direction: 'desc',
    isRatio: true,
    format: (v) => `${Math.round((v ?? 0) * 100)}%`,
    compute: (stats) => stats.winPct ?? 0,
  },
  matchesPlayed: {
    id: 'matchesPlayed',
    label: 'Matches played',
    group: 'Matches',
    description: 'Total matches played (completed)',
    modes: ['rotation', 'classic'],
    direction: 'desc',
    format: (v) => String(v ?? 0),
    compute: (stats) => stats.mp ?? 0,
  },
  mvps: {
    id: 'mvps',
    label: 'MVPs',
    group: 'Matches',
    description: 'Rotation match MVPs won',
    modes: ['rotation'],
    direction: 'desc',
    format: (v) => String(v ?? 0),
    compute: (stats) => stats.mvp ?? 0,
  },

  // --- GAMES ---
  gameWins: {
    id: 'gameWins',
    label: 'Game wins',
    group: 'Games',
    description: 'Total individual games won',
    modes: ['rotation', 'classic'],
    direction: 'desc',
    format: (v) => String(v ?? 0),
    compute: (stats) => stats.gw ?? 0,
  },
  gameWinPct: {
    id: 'gameWinPct',
    label: 'Game win %',
    group: 'Games',
    description: 'Percentage of individual games won',
    modes: ['rotation', 'classic'],
    direction: 'desc',
    isRatio: true,
    format: (v) => `${Math.round((v ?? 0) * 100)}%`,
    compute: (stats) => stats.gameWinPct ?? 0,
  },
  gameDiff: {
    id: 'gameDiff',
    label: 'Game difference',
    group: 'Games',
    description: 'Games won minus games lost',
    modes: ['rotation', 'classic'],
    direction: 'desc',
    format: (v) => {
      const num = v ?? 0;
      return `${num > 0 ? '+' : ''}${num}`;
    },
    compute: (stats) => stats.gameDiff ?? 0,
  },
  gamesPlayed: {
    id: 'gamesPlayed',
    label: 'Games played',
    group: 'Games',
    description: 'Total individual games played',
    modes: ['rotation', 'classic'],
    direction: 'desc',
    format: (v) => String(v ?? 0),
    compute: (stats) => stats.gp ?? 0,
  },

  // --- SERVE ---
  holdWins: {
    id: 'holdWins',
    label: 'Games won on serve',
    group: 'Serve',
    description: 'Service games won (holds)',
    modes: ['rotation', 'classic'],
    direction: 'desc',
    format: (v) => String(v ?? 0),
    compute: (stats) => stats.holdWins ?? 0,
  },
  holdPct: {
    id: 'holdPct',
    label: 'Hold %',
    group: 'Serve',
    description: 'Percentage of service games won',
    modes: ['rotation', 'classic'],
    direction: 'desc',
    isRatio: true,
    format: (v) => `${Math.round((v ?? 0) * 100)}%`,
    compute: (stats) => stats.holdPct ?? 0,
  },
  breakWins: {
    id: 'breakWins',
    label: 'Games won while receiving',
    group: 'Serve',
    description: 'Games won while receiving (breaks)',
    modes: ['rotation', 'classic'],
    direction: 'desc',
    format: (v) => String(v ?? 0),
    compute: (stats) => stats.breakWins ?? 0,
  },
  breakPct: {
    id: 'breakPct',
    label: 'Break %',
    group: 'Serve',
    description: 'Percentage of receiving games won',
    modes: ['rotation', 'classic'],
    direction: 'desc',
    isRatio: true,
    format: (v) => `${Math.round((v ?? 0) * 100)}%`,
    compute: (stats) => stats.breakPct ?? 0,
  },

  // --- PRESSURE ---
  goldenPointWins: {
    id: 'goldenPointWins',
    label: 'Golden-point games won',
    group: 'Pressure',
    description: 'Deciding-point games won at deuce',
    modes: ['rotation', 'classic'],
    direction: 'desc',
    format: (v) => String(v ?? 0),
    compute: (stats) => stats.goldenPointWins ?? 0,
  },
  loveGameWins: {
    id: 'loveGameWins',
    label: 'Love games won',
    group: 'Pressure',
    description: 'Games won without conceding a point',
    modes: ['rotation', 'classic'],
    direction: 'desc',
    format: (v) => String(v ?? 0),
    compute: (stats) => stats.loveGameWins ?? 0,
  },

  // --- CLASSIC ---
  setWins: {
    id: 'setWins',
    label: 'Sets won',
    group: 'Classic',
    description: 'Total sets won in classic matches',
    modes: ['classic'],
    direction: 'desc',
    format: (v) => String(v ?? 0),
    compute: (stats) => stats.setWins ?? 0,
  },
  setDiff: {
    id: 'setDiff',
    label: 'Set difference',
    group: 'Classic',
    description: 'Sets won minus sets lost in classic matches',
    modes: ['classic'],
    direction: 'desc',
    format: (v) => {
      const num = v ?? 0;
      return `${num > 0 ? '+' : ''}${num}`;
    },
    compute: (stats) => stats.setDiff ?? 0,
  },
  tiebreakWins: {
    id: 'tiebreakWins',
    label: 'Tiebreaks won',
    group: 'Classic',
    description: 'Total tiebreak games won in classic matches',
    modes: ['classic'],
    direction: 'desc',
    format: (v) => String(v ?? 0),
    compute: (stats) => stats.tiebreakWins ?? 0,
  },

  // --- FORM ---
  longestMatchStreak: {
    id: 'longestMatchStreak',
    label: 'Longest match streak',
    group: 'Form',
    description: 'Longest consecutive match winning streak in the event',
    modes: ['rotation', 'classic'],
    direction: 'desc',
    format: (v) => String(v ?? 0),
    compute: (stats) => stats.longestMatchStreak ?? 0,
  },
  longestGameStreak: {
    id: 'longestGameStreak',
    label: 'Longest game streak',
    group: 'Form',
    description: 'Longest consecutive game winning streak in the event',
    modes: ['rotation', 'classic'],
    direction: 'desc',
    format: (v) => String(v ?? 0),
    compute: (stats) => stats.longestGameStreak ?? 0,
  },
};

export const CRITERIA_GROUPS = ['Matches', 'Games', 'Serve', 'Pressure', 'Classic', 'Form'];

export function getCriterion(statId) {
  return CRITERIA_CATALOGUE[statId] || CRITERIA_CATALOGUE.matchWins;
}

export function formatStatValue(statId, value) {
  const crit = getCriterion(statId);
  return crit.format ? crit.format(value) : String(value ?? 0);
}
